import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { Currency } from '@hero/schema';
import { Input } from '../../ui/Input/Input';
import { cn } from '@hero/web-lib';
import { usePriceEdit } from '@hero/hooks-shared';
import { CurrencySymbol } from './Price';

interface PriceEditProps {
  value: number | undefined;
  onChange: (value: number | undefined) => void;
  placeholder?: string;
  disabled?: boolean;
  error?: string;
  currency?: Currency;
  className?: string;
  name?: string;
  /** Compact density for table cells. Defaults to `md`. */
  size?: 'sm' | 'md';
  /**
   * Tints the input + currency symbol to communicate direction at a glance:
   *  - `positive` (green) — income / selling rate
   *  - `info`     (blue)  — cost / buying rate
   *  - `negative` (red)   — true deficits / errors / refunds
   *  - `neutral`  (default) — no color signal
   */
  tone?: 'neutral' | 'positive' | 'info' | 'negative';
}

export const PriceEdit: React.FC<PriceEditProps> = ({
  value,
  onChange,
  placeholder,
  disabled = false,
  error,
  currency = 'SAR',
  className = '',
  name,
  size = 'md',
  tone = 'neutral',
}) => {
  const isSm = size === 'sm';
  const toneTextCls =
    tone === 'positive'
      ? 'text-emerald-600'
      : tone === 'info'
        ? 'text-sky-600'
        : tone === 'negative'
          ? 'text-rose-600'
          : 'text-foreground';
  const toneCurrencyCls =
    tone === 'positive'
      ? 'text-emerald-500/80'
      : tone === 'info'
        ? 'text-sky-500/80'
        : tone === 'negative'
          ? 'text-rose-500/80'
          : 'text-muted-foreground';
  const { t } = useTranslation(['c-price', 'c-main']);
  const inputRef = useRef<HTMLInputElement>(null);
  const cursorPositionRef = useRef<number | null>(null);

  const { inputValue, handleChange, handleBlur } = usePriceEdit({
    value,
    onChange,
    onCursorPositionChange: position => {
      cursorPositionRef.current = position;
    },
  });

  // Restore cursor position after state update
  useEffect(() => {
    if (cursorPositionRef.current !== null && inputRef.current) {
      const position = cursorPositionRef.current;
      // Use setTimeout to ensure the DOM has updated
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.setSelectionRange(position, position);
          cursorPositionRef.current = null;
        }
      }, 0);
    }
  }, [inputValue]);

  return (
    <div className={cn('space-y-1', className)}>
      <div className="relative rounded-md border border-input bg-background overflow-visible">
        {/* Currency Display */}
        <div
          className={cn(
            'absolute inset-y-0 start-0 flex items-center pointer-events-none z-10',
            isSm ? 'ps-2' : 'ps-3',
          )}>
          <span
            className={cn(
              'font-medium',
              isSm ? 'text-xs' : 'text-sm',
              toneCurrencyCls,
            )}>
            <CurrencySymbol currency={currency} />
          </span>
        </div>

        <Input
          ref={inputRef}
          type="text"
          name={name}
          value={inputValue}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder={placeholder || t('c-price:enter_price', { defaultValue: 'Enter price' })}
          disabled={disabled}
          className={cn(
            'border-0 bg-transparent focus:ring-0 focus:border-0 tabular-nums',
            isSm
              ? 'h-8 ps-7 pe-2 text-xs font-medium leading-none'
              : 'h-10 ps-12 font-medium',
            toneTextCls,
          )}
        />
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}
    </div>
  );
};
